import React, { useState, useEffect, useRef } from "react";
import LangToggle from "./LangToggle";
import ThemeToggle from "./ThemeToggle";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AhJinMark } from "./AhJinLogo";
import { openBooking } from "../lib/booking";
import {
  Menu,
  X,
  ChevronDown,
  FileBadge,
  TrendingUp,
  LineChart,
  Landmark,
  UserCog,
  Scale,
  Trophy,
  Cpu,
} from "lucide-react";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Solutions", id: "solutions", dropdown: "solutions" },
  { label: "Services", id: "services", dropdown: "mega" },
  { label: "Clients", id: "clients", route: "/clients" },
  { label: "Pricing", id: "pricing", route: "/pricing" },
  { label: "Insights", id: "insights", route: "/insights" },
  { label: "Contact", id: "contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeAll = () => {
    setOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  };

  const scrollToEl = (el) => {
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Scroll-to-section on the home page; navigate to "/" first if elsewhere.
  const go = (id) => {
    closeAll();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) scrollToEl(el);
      }, 50);
    } else {
      const el = document.getElementById(id);
      if (el) scrollToEl(el);
    }
  };

  const goRoute = (route) => {
    closeAll();
    navigate(route);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };

  const MEGA_ITEMS = [
    {
      label: "Payments & Banking",
      desc: "Cards, crypto rails & corporate banking",
      icon: Landmark,
      run: () => goRoute("/payments"),
      key: "payments",
    },
    {
      label: "Trading Platforms",
      desc: "Match-Trader, MT4 / MT5, cTrader & EXO",
      icon: LineChart,
      run: () => goRoute("/trading-platforms"),
      key: "platforms",
    },
    {
      label: "LP & Bridge",
      desc: "Centroid & FX-EDGE — liquidity wired right",
      icon: TrendingUp,
      run: () => goRoute("/lp-bridge"),
      key: "lp-bridge",
    },
    {
      label: "CRM & Back Office",
      desc: "FXBO — sales, KYC, IB networks & payments",
      icon: UserCog,
      run: () => goRoute("/crm"),
      key: "crm",
    },
    {
      label: "Legal & Compliance",
      desc: "Counsel, AML, MLRO & reporting via partner firms",
      icon: Scale,
      run: () => goRoute("/legal-compliance"),
      key: "legal",
    },
  ];

  const handleEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(key);
  };

  const SOLUTION_ITEMS = [
    { label: "Brokerage Launch", desc: "End-to-end brokerage launch services", icon: LineChart, route: "/brokerage-launch", key: "brokerage-launch" },
    { label: "Prop Firm Launch", desc: "Structure to go-live for prop firms", icon: Trophy, route: "/prop-firm-launch", key: "prop-firm-launch" },
    { label: "Incorporation", desc: "Saint Lucia, BVI, Saint Vincent & Europe", icon: FileBadge, route: "/incorporation", key: "incorporation" },
    { label: "Licensing Support", desc: "Seychelles, Mauritius, FSCA, UAE & Europe", icon: Scale, route: "/licensing", key: "licensing" },
    { label: "Platform Setup", desc: "MT4 / MT5, cTrader & Match-Trader", icon: Cpu, route: "/platform-setup", key: "platform-setup" },
    { label: "UAE & Market Entry", desc: "UAE base + cross-border set-up", icon: Landmark, route: "/uae-market-entry", key: "uae-market-entry" },
  ];

  const renderSolutionsPanel = () => (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="lux-card gold-corners pointer-events-auto relative mt-3 w-[620px] max-w-[calc(100vw-24px)] rounded-2xl shadow-[0_28px_70px_-20px_rgba(0,0,0,0.85)] overflow-hidden"
      style={{ background: "#091733" }}
      data-testid="solutions-dropdown"
      onMouseEnter={() => handleEnter("solutions")}
      onMouseLeave={handleLeave}
    >
      <div className="absolute -top-px left-8 right-8 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(229,197,111,0.6) 35%, rgba(168,85,247,0.7) 50%, rgba(229,197,111,0.6) 65%, transparent)" }} />
      <div className="relative p-2">
        <div className="px-4 py-3 border-b border-white/[0.08]">
          <span className="eyebrow-lux">Pick your path</span>
        </div>
        <ul className="py-2 grid grid-cols-2 gap-x-2">
          {SOLUTION_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key}>
                <button
                  onClick={() => goRoute(item.route)}
                  data-testid={`dropdown-solution-${item.key}`}
                  className="group relative w-full flex items-start gap-3.5 px-4 py-3 rounded-xl transition-colors text-left hover:bg-purple-600/[0.07]"
                >
                  <div
                    className="mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all"
                    style={{
                      background: "linear-gradient(135deg, rgba(229,197,111,0.10), rgba(168,85,247,0.10))",
                      border: "1px solid rgba(229,197,111,0.30)",
                      color: "#FFE9A8",
                    }}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-zinc-100 leading-tight group-hover:text-[#FFE9A8] transition-colors font-display tracking-tight">
                      {item.label}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-tight">{item.desc}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  const renderMegaPanel = () => (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="lux-card gold-corners pointer-events-auto relative mt-3 w-[620px] max-w-[calc(100vw-24px)] rounded-2xl shadow-[0_28px_70px_-20px_rgba(0,0,0,0.85)] overflow-hidden"
      style={{ background: "#091733" }}
      data-testid="mega-dropdown"
      onMouseEnter={() => handleEnter("mega")}
      onMouseLeave={handleLeave}
    >
      <div className="absolute -top-px left-8 right-8 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(229,197,111,0.6) 35%, rgba(168,85,247,0.7) 50%, rgba(229,197,111,0.6) 65%, transparent)" }} />
      <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-purple-700/30 blur-3xl pointer-events-none" />

      <div className="relative p-2">
        <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between">
          <span className="eyebrow-lux">Everything we deliver</span>
          <span
            aria-hidden="true"
            className="w-1.5 h-1.5 rotate-45"
            style={{
              background: "linear-gradient(135deg,#E5C56F,#9C7E2F)",
              boxShadow: "0 0 8px rgba(229,197,111,0.6)",
            }}
          />
        </div>
        <ul className="py-2 grid grid-cols-2 gap-x-2">
          {MEGA_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key}>
                <button
                  onClick={item.run}
                  data-testid={`dropdown-mega-${item.key}`}
                  className="group relative w-full flex items-start gap-3.5 px-4 py-3 rounded-xl transition-colors text-left hover:bg-purple-600/[0.07]"
                >
                  <div
                    className="mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all"
                    style={{
                      background: "linear-gradient(135deg, rgba(229,197,111,0.10), rgba(168,85,247,0.10))",
                      border: "1px solid rgba(229,197,111,0.30)",
                      color: "#FFE9A8",
                    }}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-zinc-100 leading-tight group-hover:text-[#FFE9A8] transition-colors font-display tracking-tight">
                      {item.label}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-tight">
                      {item.desc}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t border-white/[0.08] flex items-center justify-between">
          <button
            onClick={() => { setActiveDropdown(null); openBooking("header-dropdown"); }}
            data-testid="dropdown-mega-cta"
            className="text-[10px] tracking-[0.4em] uppercase transition-colors flourish"
            style={{ color: "#E5C56F" }}
          >
            Book a $250 Strategy Call →
          </button>
          <button
            onClick={() => goRoute("/pricing")}
            data-testid="dropdown-mega-pricing"
            className="text-[10px] tracking-[0.4em] uppercase text-zinc-400 hover:text-purple-300 transition-colors"
          >
            View pricing →
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#061027]/90 backdrop-blur-sm border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          className="cursor-pointer"
          data-testid="header-logo-btn"
        >
          <AhJinMark />
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {LINKS.map((l) =>
            l.dropdown ? (
              <div
                key={l.id}
                className="relative flex items-center leading-none"
                onMouseEnter={() => handleEnter(l.dropdown)}
                onMouseLeave={handleLeave}
              >
                <button
                  onClick={() => (l.dropdown === "mega" ? go("services") : go("paths"))}
                  data-testid={`nav-${l.id}-btn`}
                  className="inline-flex items-center gap-1.5 h-5 leading-none whitespace-nowrap text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-purple-300 transition-colors duration-300"
                  aria-haspopup="true"
                  aria-expanded={activeDropdown === l.dropdown}
                >
                  {l.label}
                  <ChevronDown
                    size={12}
                    strokeWidth={1.8}
                    className={`transition-transform duration-300 ${
                      activeDropdown === l.dropdown ? "rotate-180 text-purple-300" : ""
                    }`}
                  />
                </button>
              </div>
            ) : (
              <button
                key={l.id}
                onClick={() => (l.route ? goRoute(l.route) : go(l.id))}
                data-testid={`nav-${l.id}-btn`}
                className="inline-flex items-center h-5 leading-none whitespace-nowrap text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-purple-300 transition-colors duration-300"
              >
                {l.label}
              </button>
            )
          )}
          <LangToggle className="ml-1" />
          <ThemeToggle className="ml-1" />
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <LangToggle />
          <ThemeToggle />
          <button
            className="text-zinc-200"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Centered dropdown panels — page middle, anchored to header */}
      <div className="absolute top-full inset-x-0 hidden lg:flex justify-center pointer-events-none">
        <AnimatePresence>
          {activeDropdown === "mega" && renderMegaPanel()}
          {activeDropdown === "solutions" && renderSolutionsPanel()}
        </AnimatePresence>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#071230]/95">
          <div className="px-5 py-6 flex flex-col gap-1">
            {LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.id} className="border-b border-white/[0.06] pb-2 mb-2">
                  <button
                    onClick={() => setMobileExpanded((v) => (v === l.dropdown ? null : l.dropdown))}
                    data-testid={`mobile-nav-${l.id}-btn`}
                    className="w-full flex items-center justify-between py-3 text-zinc-300 uppercase tracking-[0.3em] text-xs"
                    aria-expanded={mobileExpanded === l.dropdown}
                  >
                    <span>{l.label}</span>
                    <ChevronDown
                      size={14}
                      strokeWidth={1.8}
                      className={`transition-transform duration-300 ${
                        mobileExpanded === l.dropdown ? "rotate-180 text-purple-300" : "text-zinc-500"
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileExpanded === l.dropdown && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        {(l.dropdown === "mega" ? MEGA_ITEMS : SOLUTION_ITEMS).map((item) => {
                          const Icon = item.icon;
                          return (
                            <li key={item.key}>
                              <button
                                onClick={item.run || (() => goRoute(item.route))}
                                data-testid={`mobile-dropdown-mega-${item.key}`}
                                className="w-full flex items-start gap-3 py-2.5 pl-1 pr-3 text-left"
                              >
                                <div className="mt-0.5 w-7 h-7 rounded-md border border-purple-500/25 bg-purple-600/10 flex items-center justify-center text-purple-300 shrink-0">
                                  <Icon size={12} strokeWidth={1.5} />
                                </div>
                                <div>
                                  <div className="text-[13px] text-zinc-200 leading-tight">{item.label}</div>
                                  <div className="text-[10px] text-zinc-500 mt-0.5">{item.desc}</div>
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  key={l.id}
                  onClick={() => (l.route ? goRoute(l.route) : go(l.id))}
                  data-testid={`mobile-nav-${l.id}-btn`}
                  className="text-left text-zinc-300 uppercase tracking-[0.3em] text-xs py-3"
                >
                  {l.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
